import { Action } from '@ngrx/store';

import { ICONS } from '@core/services/icons/icons.service';
import { closePopup } from '@core/store/data-visualization-popup/data-visualization-popup.actions';
import { PlanEditSessionActions } from '@core/store/plan-edit/plan-edit-session.actions';
import { EditAvailability } from '@core/store/plan-edit/plan-edit-session.selectors';
import { PlanEditSessionState } from '@core/store/plan-edit/plan-edit.state';

import {
  ToolboxButton,
  ToolboxDropdownButton
} from '@shared/components/toolbox/toolbox.component';

export const PLAN_EDIT_KEY = 'features.plan.edit.button';

export interface PlanEditToolboxInput {
  availability: EditAvailability;
  /** The parameter the plan records; a copied plan records none. */
  planParameterId: number | null;
  sessionMode: PlanEditSessionState['mode'];
  /** The plan's solver's parameters, read only when the plan records none; `null` until read. */
  parameters: { id: number; name?: string }[] | null;
  translate: (key: string, params?: Record<string, unknown>) => string;
  edit: (paramId: number) => void;
}

/**
 * The toolbox's entry into edit mode. GBE opens a session only with a solver parameter, the one
 * DL recomputes with: a plan that records none offers its solver's parameters to pick from, as
 * Optimize does. Gone once the session is open, where the edit bar takes over.
 */
export function planEditToolboxItem(
  input: PlanEditToolboxInput
): ToolboxButton | ToolboxDropdownButton | null {
  const { availability, planParameterId, sessionMode, parameters, translate } = input;
  if (!availability.visible || (sessionMode !== 'off' && sessionMode !== 'opening')) {
    return null;
  }
  const base = { id: 'plan-edit', ariaLabel: translate(`${PLAN_EDIT_KEY}.edit`) };

  if (sessionMode === 'opening') {
    return {
      ...base,
      icon: `${ICONS.circleNotch} animate-spin motion-reduce:animate-none`,
      tooltip: translate(`${PLAN_EDIT_KEY}.opening`),
      disabled: true,
      callback: () => {}
    };
  }
  const reason = availability.disabledReason;
  const blocked = reason ? translate(`${PLAN_EDIT_KEY}.disabled.${reason}`) : null;

  if (planParameterId !== null) {
    return {
      ...base,
      icon: ICONS.pencilSimple,
      tooltip: blocked ?? translate(`${PLAN_EDIT_KEY}.edit`),
      disabled: blocked !== null,
      callback: () => input.edit(planParameterId)
    };
  }
  const none =
    parameters?.length === 0 ? translate(`${PLAN_EDIT_KEY}.disabled.no_parameter`) : null;
  return {
    ...base,
    icon: ICONS.pencilSimple,
    tooltip: blocked ?? none ?? translate(`${PLAN_EDIT_KEY}.choose_parameter`),
    disabled: blocked !== null || parameters === null || parameters.length === 0,
    items: (parameters ?? []).map((parameter) => ({
      label:
        parameter.name ||
        translate(`${PLAN_EDIT_KEY}.unnamed_parameter`, { id: parameter.id }),
      action: () => input.edit(parameter.id)
    }))
  };
}

/** Edit mode lives on the timeline, which the popup takes out of the main window. */
export function openPlanEdit(args: {
  planId: string;
  paramId: number;
  popupOpen: boolean;
  dispatch: (action: Action) => void;
  showVisu: () => void;
}): void {
  if (args.popupOpen) args.dispatch(closePopup());
  args.showVisu();
  args.dispatch(
    PlanEditSessionActions.editRequested({ planId: args.planId, paramId: args.paramId })
  );
}
